/**
 * @file supabase.ts
 * @description Supabase client configuration and environment adapter for Venue Admin.
 * 
 * INFRASTRUCTURE & CREDENTIAL STRATEGY:
 * 1. VITE_SUPABASE_URL:
 *    Raw Supabase project endpoint (required for Realtime WebSocket channels).
 * 2. VITE_SUPABASE_API_URL:
 *    Cloudflare Worker API gateway (`api.tablegreet.com`). In production, API calls
 *    route through the Worker which validates headers and rate limits.
 * 3. VITE_SUPABASE_ANON_KEY:
 *    - Local development: Real anon key from `.env` or `.env.local` (never committed).
 *    - Cloudflare Pages Production: Set to sentinel value `WORKER_INJECTED`.
 *      The Cloudflare Worker injects the secret server-side to keep client bundles clean.
 * 4. Isolation:
 *    - `supabase`: Authenticated client holding operator/staff sessions.
 *    - `publicSupabase`: Anonymous client with session persistence disabled,
 *      used for public menu rendering to prevent permission leakage.
 */

import { createClient } from '@supabase/supabase-js';

const SENTINEL = 'WORKER_INJECTED';
const LOCAL_DEV_API_PROXY_PATH = '/__tablegreet-api';

// Resolves Supabase and TableGreet API gateway endpoints from deployment environment variables
const supabaseUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || SENTINEL;
const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_SUPABASE_API_URL || supabaseUrl;

if (!supabaseUrl) {
  throw new Error('Missing VITE_SUPABASE_URL environment variable');
}

/**
 * Validates whether a given IP address belongs to standard private RFC1918 subnets.
 * Used to detect local intranet testing devices.
 */
const isPrivateIpv4Hostname = (hostname: string) => {
  const parts = hostname.split('.').map(Number);
  if (
    parts.length !== 4 ||
    parts.some(part => !Number.isInteger(part) || part < 0 || part > 255)
  ) {
    return false;
  }

  const [a, b] = parts;
  return (
    a === 10 ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168)
  );
};

const isLocalDevelopmentHost = () => {
  if (typeof window === 'undefined' || window.location.protocol !== 'http:') {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase();
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    isPrivateIpv4Hostname(hostname)
  );
};

// Exports the unified API URL resolver so billing and edge function callers route through api.tablegreet.com
export const getSupabaseApiUrl = () => {
  if (
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    (apiUrl === 'https://api.tablegreet.com' || apiUrl === 'https://api-testing.tablegreet.com') &&
    isLocalDevelopmentHost()
  ) {
    return `${window.location.origin}${LOCAL_DEV_API_PROXY_PATH}`;
  }

  return apiUrl;
};

/**
 * Custom storage wrapper to handle "Remember Me" functionality.
 * - When user checked "Remember Me", session data is written to localStorage (persisting across browser restarts).
 * - Otherwise, session data is stored in sessionStorage (cleared when browser tab/window closes).
 * 
 * DEBUGGING TIP: If a user gets logged out after closing their browser, verify `localStorage.getItem('tg_persist')`.
 */
const tgStorage = {
  getItem: (key: string): string | null => {
    if (typeof window === 'undefined') return null;
    // Always check localStorage first for PKCE code verifiers to ensure OAuth callback survives redirects
    if (key.includes('code-verifier') || key.includes('verifier')) {
      return localStorage.getItem(key) || sessionStorage.getItem(key);
    }
    const shouldPersist = localStorage.getItem('tg_persist') === 'true';
    if (shouldPersist) {
      return localStorage.getItem(key) || sessionStorage.getItem(key);
    }
    return sessionStorage.getItem(key) || localStorage.getItem(key);
  },
  setItem: (key: string, value: string): void => {
    if (typeof window === 'undefined') return;
    // PKCE code verifiers must always be saved to localStorage so cross-origin redirects (e.g. Google OAuth) do not lose them
    if (key.includes('code-verifier') || key.includes('verifier')) {
      localStorage.setItem(key, value);
      sessionStorage.setItem(key, value);
      return;
    }
    const shouldPersist = localStorage.getItem('tg_persist') === 'true';
    if (shouldPersist) {
      localStorage.setItem(key, value);
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, value);
      localStorage.removeItem(key);
    }
  },
  removeItem: (key: string): void => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  },
};

/**
 * Primary authenticated Supabase client for administrative operations.
 */
export const supabase = createClient(getSupabaseApiUrl(), supabaseAnonKey, {
  auth: {
    storage: tgStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // Isolated session storage namespace prevents collision with chef, waiter, and dispatcher terminals
    storageKey: 'tg_venue_admin_session',
    // Bypass navigator.locks to prevent AbortError crashes in React Strict Mode.
    // React's double-invocation of effects can cause race conditions in the Auth helper
    // when attempting to acquire a lock on the storage key simultaneously.
    lock: (_name, _acquireTimeout, fn) => fn(),
  },
  db: {
    schema: 'public',
  },
  global: {
    headers: {
      'x-client-info': 'digital-menu/1.0',
    },
  },
});

/**
 * Public Menu Supabase client:
 * Public menu reads must never inherit an authenticated staff session. This
 * keeps public RLS checks on the `anon` role even when a signed-in operator
 * opens a guest menu in the same browser session.
 */
export const publicSupabase = createClient(getSupabaseApiUrl(), supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
    storageKey: 'tablegreet-public-anon',
  },
  db: {
    schema: 'public',
  },
  global: {
    headers: {
      'x-client-info': 'digital-menu-public/1.0',
    },
  },
});
